/**
 * Leviathan Web - Theme Palettes
 * Quasar Prime LLC
 *
 * Color palette definitions for the user-selectable theme system.
 * Each palette maps to a set of CSS variables applied at runtime by
 * ThemeContext. See styles/theme.css for the variable defaults.
 */

export interface Palette {
  name: string;
  description: string;
  mode: 'dark' | 'light';
  colors: {
    // Base
    bgBase: string;
    bgPanel: string;
    bgPanelHover: string;
    borderDefault: string;
    borderStrong: string;

    // Text
    textPrimary: string;
    textSecondary: string;
    textTertiary: string;

    // Primary accent (header bar, primary buttons, scheduled status)
    primary: string;
    primaryGlow: string; // For glass effects: rgba version

    // Status colors
    statusActive: string; // En route, active items
    statusActiveGlow: string;
    statusWarning: string; // Delivering, warnings
    statusWarningGlow: string;
    statusComplete: string; // Completed items
    statusCompleteGlow: string;
    statusError: string; // Errors, alerts

    // Semantic (consistent across palettes)
    success: string;
    warning: string;
    error: string;
    info: string;
  };
}

export const palettes: Record<string, Palette> = {
  cyan: {
    name: 'Cyan',
    description: 'Cool, techy, command center',
    mode: 'dark',
    colors: {
      bgBase: '#04080c',
      bgPanel: '#0a1420',
      bgPanelHover: '#101a28',
      borderDefault: '#1a2a3a',
      borderStrong: '#2a3a4a',

      textPrimary: '#ffffff',
      textSecondary: '#70a0c0',
      textTertiary: '#5080a0',

      primary: '#00d4ff',
      primaryGlow: 'rgba(0, 212, 255, 0.12)',

      statusActive: '#00e890',
      statusActiveGlow: 'rgba(0, 232, 144, 0.12)',
      statusWarning: '#ffc040',
      statusWarningGlow: 'rgba(255, 192, 64, 0.12)',
      statusComplete: '#607080',
      statusCompleteGlow: 'rgba(96, 112, 128, 0.1)',
      statusError: '#ff4050',

      success: '#00e890',
      warning: '#ffc040',
      error: '#ff4050',
      info: '#00d4ff',
    },
  },

  amber: {
    name: 'Amber',
    description: 'Warm, industrial, classic',
    mode: 'dark',
    colors: {
      bgBase: '#0a0806',
      bgPanel: '#141008',
      bgPanelHover: '#1a1610',
      borderDefault: '#2a2018',
      borderStrong: '#3a3020',

      textPrimary: '#ffffff',
      textSecondary: '#c0a080',
      textTertiary: '#a08050',

      primary: '#ffb020',
      primaryGlow: 'rgba(255, 176, 32, 0.12)',

      statusActive: '#50c878',
      statusActiveGlow: 'rgba(80, 200, 120, 0.12)',
      statusWarning: '#ff6440',
      statusWarningGlow: 'rgba(255, 100, 64, 0.12)',
      statusComplete: '#786450',
      statusCompleteGlow: 'rgba(120, 100, 80, 0.1)',
      statusError: '#ff4040',

      success: '#50c878',
      warning: '#ff6440',
      error: '#ff4040',
      info: '#ffb020',
    },
  },

  emerald: {
    name: 'Emerald',
    description: 'Tactical, military, focused',
    mode: 'dark',
    colors: {
      bgBase: '#040a08',
      bgPanel: '#0a140e',
      bgPanelHover: '#101a14',
      borderDefault: '#1a2a20',
      borderStrong: '#2a3a30',

      textPrimary: '#ffffff',
      textSecondary: '#80c0a0',
      textTertiary: '#50a070',

      primary: '#00e080',
      primaryGlow: 'rgba(0, 224, 128, 0.12)',

      statusActive: '#00c8ff',
      statusActiveGlow: 'rgba(0, 200, 255, 0.12)',
      statusWarning: '#ffc800',
      statusWarningGlow: 'rgba(255, 200, 0, 0.12)',
      statusComplete: '#607864',
      statusCompleteGlow: 'rgba(96, 120, 100, 0.1)',
      statusError: '#ff5050',

      success: '#00e080',
      warning: '#ffc800',
      error: '#ff5050',
      info: '#00c8ff',
    },
  },

  violet: {
    name: 'Violet',
    description: 'Premium, unique, modern',
    mode: 'dark',
    colors: {
      bgBase: '#08060c',
      bgPanel: '#100e18',
      bgPanelHover: '#161420',
      borderDefault: '#201a2a',
      borderStrong: '#302a3a',

      textPrimary: '#ffffff',
      textSecondary: '#b0a0d0',
      textTertiary: '#8070a0',

      primary: '#a080ff',
      primaryGlow: 'rgba(160, 128, 255, 0.12)',

      statusActive: '#50c8c8',
      statusActiveGlow: 'rgba(80, 200, 200, 0.12)',
      statusWarning: '#ffa064',
      statusWarningGlow: 'rgba(255, 160, 100, 0.12)',
      statusComplete: '#645a78',
      statusCompleteGlow: 'rgba(100, 90, 120, 0.1)',
      statusError: '#ff5080',

      success: '#50c8c8',
      warning: '#ffa064',
      error: '#ff5080',
      info: '#a080ff',
    },
  },

  crimson: {
    name: 'Crimson',
    description: 'Bold, intense, high-energy',
    mode: 'dark',
    colors: {
      bgBase: '#0a0608',
      bgPanel: '#140a0e',
      bgPanelHover: '#1a1014',
      borderDefault: '#2a1820',
      borderStrong: '#3a2830',

      textPrimary: '#ffffff',
      textSecondary: '#c08090',
      textTertiary: '#a06070',

      primary: '#ff4050',
      primaryGlow: 'rgba(255, 64, 80, 0.12)',

      statusActive: '#00c8f0',
      statusActiveGlow: 'rgba(0, 200, 240, 0.12)',
      statusWarning: '#ffb000',
      statusWarningGlow: 'rgba(255, 176, 0, 0.12)',
      statusComplete: '#78646e',
      statusCompleteGlow: 'rgba(120, 100, 110, 0.1)',
      statusError: '#ff2040',

      success: '#00c8f0',
      warning: '#ffb000',
      error: '#ff2040',
      info: '#ff4050',
    },
  },

  light: {
    name: 'Light',
    description: 'Bright, accessible, daytime',
    mode: 'light',
    colors: {
      bgBase: '#f4f6f8',
      bgPanel: '#ffffff',
      bgPanelHover: '#e8ecf0',
      borderDefault: '#d0d8e0',
      borderStrong: '#b0b8c0',

      textPrimary: '#1a2a3a',
      textSecondary: '#4a5a6a',
      textTertiary: '#6a7a8a',

      primary: '#0080c0',
      primaryGlow: 'rgba(0, 128, 192, 0.08)',

      statusActive: '#00a050',
      statusActiveGlow: 'rgba(0, 160, 80, 0.08)',
      statusWarning: '#dc8c00',
      statusWarningGlow: 'rgba(220, 140, 0, 0.08)',
      statusComplete: '#8090a0',
      statusCompleteGlow: 'rgba(128, 144, 160, 0.06)',
      statusError: '#d03040',

      success: '#00a050',
      warning: '#dc8c00',
      error: '#d03040',
      info: '#0080c0',
    },
  },
};

// Font size presets
export const fontSizes = {
  small: '14px',
  medium: '16px',
  large: '18px',
  xlarge: '20px',
};
